import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

// Styles
import useStyles from "../../../../assets/styles/components/cards/statusCourseCard/statusCourseContentStyle";
import qualificationIcon from "../../../../assets/images/icons/qualification.svg";
// Material components
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Icon,
  IconButton,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

// Constants
import { ACCESS_ACTION } from "../../../../constants/access-action";
import { ENROLLMENT_STATUS } from "../../../../constants/enrollment-status";
import { MEMBERSHIPS } from "../../../../../shared/constants/memberships";

const StatusCourseContent = (props) => {
  const {
    approved,
    companyId,
    completed,
    content,
    onSaveCourse,
    isLogged,
    userId,
    courseId,
    enrollmentStatusId,
    onClickGoToCourse,
    onClickRegister,
    onClickGoToDetail,
    noAvailable = 0,
    tagPeriodo = "CURSO",
    userMembership,
    isSecondEnrrollment,
    grade = null,
    showDetailButton,
    blocked,
    blockedReason,
    isEstadia = false,
    estadiaLink,
    startDate,
    merbershipIsExpired,
    handleRenovationMembership,
    textbuttonRenovation,
    accessAction = null,
    onClickUpgrade,
  } = props;
  const { isOptative, enrollmentNotAvailable, isEnrrollment = 0 } = content;
  const classes = useStyles();
  const theme = useTheme();
  const isResponsive = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const [saveStatus, setSaveStatus] = React.useState(false);
  const [updatingStatus, setUpdatingStatus] = React.useState(false);
  const [isProcessingRegister, setIsProcessingRegister] = React.useState(false);
  const [isProcessingRenovation, setIsProcessingRenovation] = React.useState(false);

  const handleSaveUnsaveCourse = async (e) => {
    const newStatus = !saveStatus;
    setUpdatingStatus(true);
    setSaveStatus(newStatus);
    const response = await onSaveCourse(e, newStatus, userId, courseId);
    setSaveStatus(response);
    setUpdatingStatus(false);
  };

  const processRenovationLoader = async (e) => {
    setIsProcessingRenovation(true);
    await handleRenovationMembership(e);
    setIsProcessingRenovation(false);
  };

  // Acción de acceso al curso. El contenedor la calcula a partir de los datos de
  // entitlement del API (typeLabel / is_included para cursos, membresia_id para
  // programas). Cuando no se envía, se deriva del estado de la membresía para
  // conservar el comportamiento de las vistas que aún no la pasan.
  const resolvedAccessAction = accessAction ?? (merbershipIsExpired ? ACCESS_ACTION.RENEW : ACCESS_ACTION.ENROLL);

  const UpgradeButton = () => (
    <Button variant="contained" color="primary" size="small" fullWidth onClick={onClickUpgrade}>
      Mejora tu plan
    </Button>
  );

  const DetailButton = () => (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      fullWidth
      onClick={onClickGoToDetail ?? onClickRegister}
    >
      Ver detalle
    </Button>
  );

  const processRegisterLoader = async (e) => {
    setIsProcessingRegister(true);
    await onClickRegister(e);
    setIsProcessingRegister(false);
  };

  const RenovationButton = () => (
    <Button
      variant="contained"
      color="primary"
      size="small"
      fullWidth
      disabled={isProcessingRenovation}
      onClick={processRenovationLoader}
      style={isProcessingRenovation ? { backgroundColor: theme.palette.primary.main, opacity: 0.8 } : undefined}
    >
      {isProcessingRenovation ? (
        <CircularProgress style={{ color: "white", maxHeight: "16px", maxWidth: "16px" }} />
      ) : (
        `${textbuttonRenovation} membresía`
      )}
    </Button>
  );

  const ButtonByStatus = () => {
    if (blocked) return null;

    // Estadía empresarial: lógica exclusiva basada en progress_percentage y grade
    if (isEstadia) {
      if (completed) {
        // progress_percentage === 100: approved decide
        if (!approved) {
          // Reprobada — mostrar calificación + link si tiene estadiaLink
          return (
            <>
              {grade !== null && (
                <Box display="flex" gridGap={8} mb={1}>
                  <img src={qualificationIcon} width={16} height={16} alt="" />
                  <Typography variant="body2">Calificación: {grade}</Typography>
                </Box>
              )}
              {estadiaLink && (
                <Typography
                  variant="body2"
                  className="semi-bold"
                  style={{ color: theme.palette.colors.orange[300], cursor: "pointer", textAlign: "center" }}
                  onClick={() => window.open(estadiaLink, "_blank")}
                >
                  ¿Cómo me inscribo?
                </Typography>
              )}
            </>
          );
        }
        // Aprobada — solo mostrar calificación, sin botones
        return (
          <Box display="flex" gridGap={8}>
            <img src={qualificationIcon} width={16} height={16} alt="" />
            <Typography variant="body2">Calificación: {grade}</Typography>
          </Box>
        );
      }
      // progress_percentage < 100: verificar si tiene link de inscripción (pendiente) o liga neo (en progreso)
      if (estadiaLink) {
        return (
          <Typography
            variant="body2"
            className="semi-bold"
            style={{ color: theme.palette.colors.orange[300], cursor: "pointer", textAlign: "center" }}
            onClick={() => window.open(estadiaLink, "_blank")}
          >
            ¿Cómo me inscribo?
          </Typography>
        );
      }


      if (merbershipIsExpired) {
        return <RenovationButton />;
      }

      if (onClickGoToCourse) {
        return (
          <Button variant="outlined" color="secondary" size="small" fullWidth onClick={onClickGoToCourse}>
            Ir al curso
          </Button>
        );
      }
      return null;
    }

    // Sin derecho de acceso no se ofrece entrar ni reinscribirse. La calificación
    // ya obtenida sí se muestra: es información que el alumno se ganó, no un
    // beneficio de la membresía.
    if (resolvedAccessAction === ACCESS_ACTION.UPGRADE || resolvedAccessAction === ACCESS_ACTION.RENEW) {
      const hasGrade = completed && grade !== null && grade !== false;

      return (
        <>
          {hasGrade && (
            <Box display="flex" gridGap={8} mb={1}>
              <img src={qualificationIcon} width={16} height={16} alt="" />
              <Typography variant="body2">Calificación: {grade}</Typography>
            </Box>
          )}
          {resolvedAccessAction === ACCESS_ACTION.UPGRADE ? <UpgradeButton /> : <RenovationButton />}
        </>
      );
    }

    if (completed && approved) {
      if (companyId === null && onClickGoToCourse !== undefined) {
        return (
          <Button variant="outlined" color="secondary" size="small" fullWidth onClick={onClickGoToCourse}>
            Ver curso terminado
          </Button>
        );
      } else if (companyId !== null){
        return (
          <Box display="flex" gridGap={8}>
            <img src={qualificationIcon} width={16} height={16} alt="" />
            <Typography variant="body2">Calificación: {grade}</Typography>
          </Box>
        );
      }
    } else if (completed && !approved) {
      return (
        <>
          <Box display="flex" gridGap={8} mb={1}>
            <img src={qualificationIcon} width={16} height={16} alt="" />
            <Typography variant="body2">Calificación: {grade}</Typography>
          </Box>
          {isEnrrollment === 0 ? (
            <Button variant="outlined" color="secondary" size="small" fullWidth disabled>
              <Icon
                className="ri-lock-2-line text-fills-strokes-200 font-size-20"
                style={{ marginRight: "5px" }}
              />
              Próximamente
            </Button>
          ) : (
            onClickRegister && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                fullWidth
                startIcon={isSecondEnrrollment === 0 ? <Icon className="ri-lock-2-line" /> : null}
                onClick={processRegisterLoader}
                disabled={isSecondEnrrollment === 0 || noAvailable === 1}
              >
                Volver a cursar
              </Button>
            )
          )}
        </>
      );
    } else {
      return (
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          fullWidth
          onClick={onClickGoToCourse}
          disabled={!!completed}
          startIcon={!!completed && <Icon className="ri-lock-2-line" />}
        >
          Ir al curso
        </Button>
      );
    }

    return null;
  };

  const canShowEnrollBlock =
    enrollmentStatusId !== ENROLLMENT_STATUS.ENROLLED &&
    enrollmentStatusId !== ENROLLMENT_STATUS.PAUSED &&
    noAvailable === 0 &&
    isEnrrollment !== 0;

  return (
    <Box className={classes.rootStatusCourseContent}>
      <Box className={classes.headerContainer}>
        <Chip label={tagPeriodo} className={"chip3-blue-300-inverted"} />
        {isLogged && !!onSaveCourse && (
          <IconButton disabled={updatingStatus} onClick={handleSaveUnsaveCourse} className="p-0">
            <Icon
              className={clsx(
                saveStatus ? "ri-bookmark-fill text-violet-300" : "ri-bookmark-line text-dark-black-200",
                `font-size-${isResponsive ? "20" : "24"}`
              )}
            />
          </IconButton>
        )}
      </Box>
      <Box className={classes.courseNameContainer}>
        <Typography
          variant="body1"
          className={clsx("semi-bold", isResponsive && "variant-xsmall", classes.courseName)}
        >
          {content.courseName}
        </Typography>
      </Box>

      {isEstadia && startDate && (
        <Box mt={0}>
          <Typography
            variant="overline"
            className="semi-bold"
            style={{ color: theme.palette.colors.blue[300], textTransform: "uppercase", marginBottom: "8px" }}
          >
            FECHA DE INICIO:
          </Typography>
          <Box display="flex" alignItems="center" gridGap={8} mb={1}>
            <Icon className="ri-calendar-2-line text-white font-size-12" />
            <Typography
              variant="overline"
              className="semi-bold"
              style={{ color: theme.palette.colors.white, textTransform: "uppercase" }}
            >
              {startDate}
            </Typography>
          </Box>
        </Box>
      )}

      <Box>
        <Box>
          {blocked && (
            <Box className={classes.blockedMessageContainer}>
              <ErrorOutlineIcon style={{ color: "#FCDC43", fontSize: "16px" }} aria-hidden />
              <Typography variant="caption" className={classes.blockedMessageText}>
                {blockedReason || "Debes aprobar los prerrequisitos indicados para inscribirte a este curso."}
              </Typography>
            </Box>
          )}

          {/* Curso accesible: mostrar botón Empezar curso */}
          {!blocked && canShowEnrollBlock && resolvedAccessAction === ACCESS_ACTION.ENROLL && (
            isEstadia && estadiaLink ? (
              <Typography
                variant="body2"
                className="semi-bold"
                style={{ color: theme.palette.colors.orange[300], cursor: "pointer", textAlign: "center" }}
                onClick={() => window.open(estadiaLink, "_blank")}
              >
                ¿Cómo me inscribo?
              </Typography>
            ) : (
              <Button
                variant={
                  userMembership &&
                  userMembership.id > MEMBERSHIPS.FREEMIUM &&
                  (!userMembership.is_active || userMembership?.subscription_past_due)
                    ? "outlined"
                    : "contained"
                }
                color={
                  userMembership &&
                  userMembership.id > MEMBERSHIPS.FREEMIUM &&
                  (!userMembership.is_active || userMembership?.subscription_past_due)
                    ? "secondary"
                    : "primary"
                }
                size="small"
                fullWidth
                disabled={
                  userMembership &&
                  userMembership.id > MEMBERSHIPS.FREEMIUM &&
                  (!userMembership.is_active || userMembership?.subscription_past_due)
                }
                onClick={processRegisterLoader}
              >
                {isProcessingRegister ? (
                  <CircularProgress style={{ color: "white", maxHeight: "16px", maxWidth: "16px" }} />
                ) : isOptative ? (
                  "Inscribirme ahora"
                ) : (
                  "Empezar curso"
                )}
              </Button>
            )
          )}

          {/* Membresía expirada: mostrar botón Reactivar membresía */}
          {!blocked && canShowEnrollBlock && resolvedAccessAction === ACCESS_ACTION.RENEW && (
            <RenovationButton />
          )}

          {/* Curso incluido en otra membresía: invitar a mejorar el plan */}
          {!blocked && canShowEnrollBlock && resolvedAccessAction === ACCESS_ACTION.UPGRADE && (
            <UpgradeButton />
          )}

          {/* Curso que se vende por separado: enviar al detalle */}
          {!blocked && canShowEnrollBlock && resolvedAccessAction === ACCESS_ACTION.DETAIL && (
            <DetailButton />
          )}

          {!blocked && !!enrollmentStatusId && (enrollmentStatusId === ENROLLMENT_STATUS.ENROLLED || enrollmentStatusId === ENROLLMENT_STATUS.PAUSED) && (
            <ButtonByStatus />
          )}

          {!blocked &&
            (!enrollmentStatusId || (enrollmentStatusId !== ENROLLMENT_STATUS.ENROLLED && enrollmentStatusId !== ENROLLMENT_STATUS.PAUSED)) &&
            (noAvailable === 1 || isEnrrollment === 0) && (
              <Button variant="outlined" color="secondary" size="small" fullWidth disabled>
                <Icon
                  className="ri-lock-2-line text-fills-strokes-200 font-size-20"
                  style={{ marginRight: "5px" }}
                />
                {noAvailable === 1 ? "Próximamente" : "Inscribirme ahora"}
              </Button>
            )}
        </Box>
        <Box mt="16px">
          {showDetailButton && (
            <Button color="primary" fullWidth onClick={onClickGoToDetail}>
              Ver detalle
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

StatusCourseContent.propTypes = {
  content: PropTypes.shape({
    courseName: PropTypes.string.isRequired,
  }).isRequired,
  onSaveCourse: PropTypes.func,
  isLogged: PropTypes.bool,
  courseId: PropTypes.number.isRequired,
  userId: PropTypes.number,
  enrollmentStatusId: PropTypes.number,
  onClickGoToCourse: PropTypes.func,
  onClickRegister: PropTypes.func,
  onClickGoToDetail: PropTypes.func,
  showDetailButton: PropTypes.bool,
  blocked: PropTypes.bool,
  blockedReason: PropTypes.string,
  isEstadia: PropTypes.bool,
  estadiaLink: PropTypes.string,
  startDate: PropTypes.string,
  accessAction: PropTypes.oneOf(Object.values(ACCESS_ACTION)),
  onClickUpgrade: PropTypes.func,
};

export default StatusCourseContent;
